import React from "react";
import { closeHealthLabels } from "@common/types/closeHealth";
import { closeProgressStatusLabels } from "@common/types/closeProgress";
import { CounterpartyDisabledFilter } from "@common/constants/counterparty";
import { CoaAccountFilterLabel } from "@components/pageHeader/filters/ActiveFilters/CoaAccountFilterLabel";
import { GeneralLedgerFilterLabel } from "@components/pageHeader/filters/ActiveFilters/GeneralLedgerFilterLabel";
import { CatalogItemLedgerAccountFilterLabel } from "@components/pageHeader/filters/ActiveFilters/CatalogItemLedgerAccountFilterLabel";
import {
    AccountSummaryLabel,
    ClassSummaryLabel,
    Form1099BoxSummaryLabel,
} from "@components/pageHeader/filters/ActiveFilters/SelectionSummaryFilterLabels";
import { AccrualCoaAccountFilterLabel } from "@components/pageHeader/filters/ActiveFilters/AccrualCoaAccountFilterLabel";
import { FiltersTypes } from "@common/types/filters/general";
import { OverDueDateFilterLabel } from "@components/pageHeader/filters/Tabs/OverDueDateFilter/OverDueDateFilterLabel";
import { BillStatusFilterLabel } from "@components/pageHeader/filters/ActiveFilters/BillStatusFilterLabel";
import { CombinedStatusFilterLabel } from "@components/pageHeader/filters/ActiveFilters/CombinedStatusFilterLabel";
import { readBillStatusFilter } from "@helpers/billStatusFilter";
import { DueDateFilterLabel } from "@components/pageHeader/filters/Tabs/DueDateFilter/DueDateFilterLabel";
import { SubaccountsFilterLabel } from "@components/pageHeader/filters/ActiveFilters/SubaccountsFilterLabel";
import { TransactionLockedFilterLabel } from "@components/pageHeader/filters/ActiveFilters/TransactionLockedFilterLabel";
import { TransactionStatusFilterLabel } from "@components/pageHeader/filters/ActiveFilters/TransactionStatusFilterLabel";
import { UnreadFilterLabel } from "@components/pageHeader/filters/ActiveFilters/UnreadFilterLabel";
import { DocumentCategoriesFilterLabel } from "@components/pageHeader/filters/ActiveFilters/DocumentCategoriesFilterLabel";
import { AccountSourceFilterLabel } from "@components/accounting/AccountingPageFilters/AccountSourceFilterLabel";
import { AccountRolePresenceFilterLabel } from "@components/accounting/AccountingPageFilters/AccountRolePresenceFilterLabel";
import { Form1099BoxFilterLabel } from "@components/accounting/AccountingPageFilters/Form1099BoxFilterLabel";
import { AccountRulePresenceFilterLabel } from "@components/accounting/AccountingPageFilters/AccountRulePresenceFilterLabel";
import { JournalEntrySourceFilterLabel } from "@components/pageHeader/filters/ActiveFilters/JournalEntrySourceFilterLabel";
import { AmountFilterLabel } from "@components/pageHeader/filters/ActiveFilters/AmountFilterLabel";
import { CounterpartiesNamedFilterLabel } from "@components/pageHeader/filters/ActiveFilters/CounterpartiesNamedFilterLabel";
import { CustomerFilterLabel } from "@components/pageHeader/filters/ActiveFilters/CustomerFilterLabel";
import { CounterpartyCreatedByFilterLabel } from "@components/pageHeader/filters/ActiveFilters/CounterpartyCreatedByFilterLabel";
import { DateFilterLabel } from "@components/pageHeader/filters/ActiveFilters/DateFilterLabel";
import { MatchedFilterLabel } from "@components/pageHeader/filters/ActiveFilters/MatchedFilterLabel";
import { MatchStatusFilterLabel } from "@components/pageHeader/filters/ActiveFilters/MatchStatusFilterLabel";
import { CashItemTypeFilterLabel } from "@components/pageHeader/filters/ActiveFilters/CashItemTypeFilterLabel";
import { NoteFilterLabel } from "@components/pageHeader/filters/ActiveFilters/NoteFilterLabel";
import { ReceiptsFilterLabel } from "@components/pageHeader/filters/ActiveFilters/ReceiptsFilterLabel";
import { SplittedFilterLabel } from "@components/pageHeader/filters/ActiveFilters/SplittedFilterLabel";
import { ReconciledFilterLabel } from "@components/pageHeader/filters/ActiveFilters/ReconciledFilterLabel";
import { useAggregatedFiltersContext } from "@components/pageHeader/filters/AggregatedFilters/AggregatedFilters.context";
import { AccountsFilterLabel } from "@components/pageHeader/filters/ActiveFilters/AccountsFilterLabel";
import { ActiveFilterButton } from "@components/pageHeader/filters/ActiveFilters/ActiveFilterButton";
import {
    DirectionFilterLabel,
    type DirectionLabelVariant,
} from "@components/pageHeader/filters/ActiveFilters/DirectionFilterLabel";
import { RoundingFilterLabel } from "@components/pageHeader/filters/ActiveFilters/RoundingFilterLabel";
import { RulesFilterLabel } from "@components/pageHeader/filters/ActiveFilters/RulesFilterLabel";
import { SimilarToFilterLabel } from "@components/pageHeader/filters/ActiveFilters/SimilarToFilterLabel";
import { CategoryFilterLabel } from "@components/pageHeader/filters/ActiveFilters/CategoryFilterLabel";
import { ClassFilterLabel } from "@components/pageHeader/filters/ActiveFilters/ClassFilterLabel";
import { useResetFilters } from "@components/pageHeader/filters/ActiveFilters/useResetFilters";
import {
    DocumentImportedFilterLabel,
    DocumentReconciledFilterLabel,
    DocumentMatchedFilterLabel,
} from "@components/pageHeader/filters/ActiveFilters/StatusFilterLabel";
import { InvoiceStatusFilterLabel } from "@components/pageHeader/filters/ActiveFilters/InvoiceStatusFilterLabel";
import { readInvoiceStatusFilter } from "@helpers/invoiceStatusFilter";
import { BillableExpenseInvoiceStatusFilterLabel } from "@components/pageHeader/filters/ActiveFilters/BillableExpenseInvoiceStatusFilterLabel";
import { CatalogItemFilterLabel } from "@components/pageHeader/filters/ActiveFilters/CatalogItemFilterLabel";
import { InvoiceSourceTemplateFilterLabel } from "@components/pageHeader/filters/ActiveFilters/InvoiceSourceTemplateFilterLabel";
import { SubscriptionFilterLabel } from "@components/pageHeader/filters/ActiveFilters/SubscriptionFilterLabel";
import { Eligible1099FilterLabel } from "@components/pageHeader/filters/ActiveFilters/Eligible1099FilterLabel";
import { RulesStatusFilterLabel } from "@components/pageHeader/filters/ActiveFilters/RulesStatusFilterLabel";
import { RulesSourceFilterLabel } from "@components/pageHeader/filters/ActiveFilters/RulesSourceFilterLabel";
import { RulesActionFilterLabel } from "@components/pageHeader/filters/ActiveFilters/RulesActionFilterLabel";
import { TransactionIdsFilterLabel } from "@components/pageHeader/filters/ActiveFilters/TransactionIdsFilterLabel";
import { BillIdsFilterLabel } from "@components/pageHeader/filters/ActiveFilters/BillIdsFilterLabel";
import { InvoiceIdsFilterLabel } from "@components/pageHeader/filters/ActiveFilters/InvoiceIdsFilterLabel";
import { TemplateIdsFilterLabel } from "@components/pageHeader/filters/ActiveFilters/TemplateIdsFilterLabel";
import { StartDateFilterLabel } from "@components/pageHeader/filters/ActiveFilters/StartDateFilterLabel";
import { TeamMembersFilterLabel } from "@components/pageHeader/filters/ActiveFilters/TeamMembersFilterLabel";
import { PlanFilterLabel } from "@components/pageHeader/filters/ActiveFilters/PlanFilterLabel";
import { WhoPaysFilterLabel } from "@components/pageHeader/filters/ActiveFilters/WhoPaysFilterLabel";
import { ServicesFilterLabel } from "@components/pageHeader/filters/ActiveFilters/ServicesFilterLabel";
import { CloseStageFilterLabel } from "@components/pageHeader/filters/ActiveFilters/CloseStageFilterLabel";
import { PriorityFilterLabel } from "@components/pageHeader/filters/ActiveFilters/PriorityFilterLabel";
import { CloseDateFilterLabel } from "@components/pageHeader/filters/ActiveFilters/CloseDateFilterLabel";
import { AssigneeFilterLabel } from "@components/pageHeader/filters/ActiveFilters/AssigneeFilterLabel";
import { TaskAssigneeFilterLabel } from "@components/pageHeader/filters/ActiveFilters/TaskAssigneeFilterLabel";
import { TaskCreatedByFilterLabel } from "@components/pageHeader/filters/ActiveFilters/TaskCreatedByFilterLabel";
import { TaskCreatedOnFilterLabel } from "@components/pageHeader/filters/ActiveFilters/TaskCreatedOnFilterLabel";
import { TaskCompletedByFilterLabel } from "@components/pageHeader/filters/ActiveFilters/TaskCompletedByFilterLabel";
import { TaskCompletedOnFilterLabel } from "@components/pageHeader/filters/ActiveFilters/TaskCompletedOnFilterLabel";
import { TemplateOwnerFilterLabel } from "@components/pageHeader/filters/ActiveFilters/TemplateOwnerFilterLabel";
import { TemplateLastEditedByFilterLabel } from "@components/pageHeader/filters/ActiveFilters/TemplateLastEditedByFilterLabel";
import { TemplateCreatedByFilterLabel } from "@components/pageHeader/filters/ActiveFilters/TemplateCreatedByFilterLabel";
import { TaskCloseFilterLabel } from "@components/pageHeader/filters/ActiveFilters/TaskCloseFilterLabel";
import { TaskPriorityFilterLabel } from "@components/pageHeader/filters/ActiveFilters/TaskPriorityFilterLabel";
import { TaskGroupFilterLabel } from "@components/pageHeader/filters/ActiveFilters/TaskGroupFilterLabel";
import { BooleanFilterLabel } from "@components/pageHeader/filters/ActiveFilters/BooleanFilterLabel";
import { TaskStageFilterLabel } from "@components/pageHeader/filters/ActiveFilters/TaskStageFilterLabel";
import {
    OrgTaskTemplateAssigneeRoleFilterLabel,
    OrgTaskTemplateCreatedByFilterLabel,
    OrgTaskTemplateGroupFilterLabel,
    OrgTaskTemplateTypeFilterLabel,
} from "@components/pageHeader/filters/ActiveFilters/OrgTaskTemplateFilterLabels";
import { LegalTypeFilterLabel } from "@components/pageHeader/filters/ActiveFilters/LegalTypeFilterLabel";
import { DocumentPeriodFilterLabel } from "@components/pageHeader/filters/ActiveFilters/DocumentPeriodFilterLabel";
import { DueDateRangeFilterLabel } from "@components/pageHeader/filters/ActiveFilters/DueDateRangeFilterLabel";
import { UploaderFilterLabel } from "@components/pageHeader/filters/ActiveFilters/UploaderFilterLabel";
import { DocumentTypeFilterLabel } from "@components/pageHeader/filters/ActiveFilters/DocumentTypeFilterLabel";
import { OrgServiceFilterLabel } from "@components/pageHeader/filters/ActiveFilters/OrgServiceFilterLabel";
import { OrgCloseStageFilterLabel } from "@components/pageHeader/filters/ActiveFilters/OrgCloseStageFilterLabel";
import { CloseTemplateFilterLabel } from "@components/pageHeader/filters/ActiveFilters/CloseTemplateFilterLabel";
import { ApplicationTypeFilterLabel } from "@components/pageHeader/filters/ActiveFilters/ApplicationTypeFilterLabel";
import { EnumFilterLabel } from "@components/pageHeader/filters/ActiveFilters/EnumFilterLabel";
import { closeStatusLabels } from "@common/types/closeStatus";
import { ClientIdsFilterLabel } from "@components/pageHeader/filters/ActiveFilters/ClientIdsFilterLabel";
import { WorkStartDateFilterLabel } from "@components/pageHeader/filters/ActiveFilters/WorkStartDateFilterLabel";
import { WorkDeliveryDateFilterLabel } from "@components/pageHeader/filters/ActiveFilters/WorkDeliveryDateFilterLabel";
import { ApplicationDateFilterLabel } from "@components/pageHeader/filters/ActiveFilters/ApplicationDateFilterLabel";
import { DaysInStageFilterLabel } from "@components/pageHeader/filters/ActiveFilters/DaysInStageFilterLabel";
import { RuleReviewFilterLabel } from "@components/pageHeader/filters/ActiveFilters/RuleReviewFilterLabel";
import { ReviewFilterLabel } from "@components/pageHeader/filters/ActiveFilters/ReviewFilterLabel";
import { ReviewerFilterLabel } from "@components/pageHeader/filters/ActiveFilters/ReviewerFilterLabel";
import { CounterpartyReviewFilterLabel } from "@components/pageHeader/filters/ActiveFilters/CounterpartyReviewFilterLabel";
import { EngagementTeamFilterLabel } from "@components/pageHeader/filters/ActiveFilters/EngagementTeamFilterLabel";
import { SchedulePeriodFilterLabel } from "@components/pageHeader/filters/ActiveFilters/SchedulePeriodFilterLabel";
import { ScheduleTemplateFilterLabel } from "@components/pageHeader/filters/ActiveFilters/ScheduleTemplateFilterLabel";
import { ScheduleClientFilterLabel } from "@components/pageHeader/filters/ActiveFilters/ScheduleClientFilterLabel";
import { InvoiceSentFilterLabel } from "@components/pageHeader/filters/ActiveFilters/InvoiceSentFilterLabel";
import { formatFilterLabelText } from "@components/pageHeader/filters/ActiveFilters/formatFilterLabel";
import { CloseTemplateBasis } from "@common/schemas/close-template.schema";
import { ClassFilterType } from "@common/types/class";
import { NATIVE_INVOICE_TERM_TYPE_LABELS } from "@common/helpers/invoice-terms.helper";
import { AR_AGING_BUCKET_LABELS } from "@common/types/reports/ar-aging";
import { getAccountingBasisLabel } from "@common/helpers/accountingBasis";
import { industryLabels } from "@components/entity/constants";
import { pluralize } from "@common/helpers/string";
import {
    HoldingAccountFilterLabel,
    SchedulesReportGroupFilterLabel,
    SchedulesReportTemplateFilterLabel,
    TermDateFilterLabel,
} from "@components/pageHeader/filters/ActiveFilters/SchedulesReportFilterLabels";
import { AccountStatusFilterLabel } from "../../accounting/AccountingPageFilters/AccountStatusFilterLabel";
import { AccountCodeFilterLabel } from "../../accounting/AccountingPageFilters/AccountFilterLabel";
import {
    ActiveFilter,
    ActiveFilterToFilterTabsMap,
    FiltersChangeFn,
    FilterTabs,
    resetPayload,
    VIRTUAL_FILTER_TABS,
} from "./lib";
import { getFilterTabLabel } from "./FilterTabLabelMapping";
import { FilterMultiselectOption } from "./FilterMultiselect/FilterMultiselect";
import {
    DraftCounterpartyFilterLabel,
    DraftOptionsFilterLabel,
} from "./Tabs/AccrualDraftFilters/AccrualDraftFilters";

export type SelectionLabelVariant = "summary";

interface Props {
    filterId: ActiveFilter;
    filters: FiltersTypes;
    onChange: FiltersChangeFn;
    disabled?: boolean;
    directionLabelVariant?: DirectionLabelVariant;
    selectionLabelVariant?: SelectionLabelVariant;
    /**
     * Word used in place of "counterparty" for the counterparty filter chip
     * text (e.g. "customer" on Invoices, "vendor" on Bills).
     */
    counterpartyEntityLabel?: string;
    tabLabels?: Partial<Record<FilterTabs, string>>;
    draftStatusOptions?: FilterMultiselectOption[];
    draftSourceOptions?: FilterMultiselectOption[];
}

type LabelProps = Pick<
    Props,
    | "filterId"
    | "filters"
    | "directionLabelVariant"
    | "selectionLabelVariant"
    | "counterpartyEntityLabel"
    | "tabLabels"
    | "draftStatusOptions"
    | "draftSourceOptions"
>;

export const getActiveFilterLabel = ({
    filterId,
    filters,
    directionLabelVariant,
    selectionLabelVariant,
    counterpartyEntityLabel,
    tabLabels,
    draftStatusOptions = [],
    draftSourceOptions = [],
}: LabelProps): React.ReactNode => {
    let label: React.ReactNode;

    // eslint-disable-next-line sonarjs/max-switch-cases
    switch (filterId) {
        case "amount":
            label = <AmountFilterLabel filters={filters} />;
            break;

        case "category":
            label = <CategoryFilterLabel filters={filters} />;
            break;

        case "date":
            label = <DateFilterLabel filters={filters} />;
            break;

        case "classes":
            label =
                selectionLabelVariant === "summary" ? (
                    <ClassSummaryLabel filters={filters} />
                ) : (
                    <ClassFilterLabel filters={filters} />
                );
            break;

        case "direction":
            label = (
                <DirectionFilterLabel
                    filters={filters}
                    labelVariant={directionLabelVariant}
                />
            );
            break;

        case "rounding":
            label = <RoundingFilterLabel filters={filters} />;
            break;

        case "rules":
            label = <RulesFilterLabel filters={filters} />;
            break;

        case "similarTo":
            label = <SimilarToFilterLabel filters={filters} />;
            break;

        case "counterparties":
            label = (
                <CounterpartiesNamedFilterLabel
                    filters={filters}
                    entityLabel={counterpartyEntityLabel}
                />
            );
            break;

        case "counterpartyCreatedBy":
            label = <CounterpartyCreatedByFilterLabel filters={filters} />;
            break;

        case "customers":
            label = <CustomerFilterLabel filters={filters} />;
            break;

        case "transactionLocked":
            label = <TransactionLockedFilterLabel filters={filters} />;
            break;

        case "matchTypes":
            label = <MatchedFilterLabel filters={filters} />;
            break;

        case "isSplitted":
            label = <SplittedFilterLabel filters={filters} />;
            break;

        case "isReconciled":
            label = <ReconciledFilterLabel filters={filters} />;
            break;

        case "transactionTypes":
            label = <CashItemTypeFilterLabel filters={filters} />;
            break;

        case "note":
            label = <NoteFilterLabel filters={filters} />;
            break;

        case "receipts":
            label = <ReceiptsFilterLabel filters={filters} />;
            break;

        case "accounts":
            label = <AccountsFilterLabel filters={filters} />;
            break;

        case "subaccounts":
            label = <SubaccountsFilterLabel filters={filters} />;
            break;

        case "documentImported":
            label = <DocumentImportedFilterLabel filters={filters} />;
            break;

        case "documentReconciled":
            label = <DocumentReconciledFilterLabel filters={filters} />;
            break;

        case "documentMatched":
            label = <DocumentMatchedFilterLabel filters={filters} />;
            break;

        case "documentCategoryIds":
            label = <DocumentCategoriesFilterLabel filters={filters} />;
            break;

        case "accountCodes":
            label = (
                <AccountCodeFilterLabel
                    accountCodes={filters.accountCodes}
                    entityId={filters.entityId}
                />
            );
            break;

        case "accountStatus":
            label = <AccountStatusFilterLabel filters={filters} />;
            break;

        case "accountSource":
            label = <AccountSourceFilterLabel filters={filters} />;
            break;

        case "accountRolePresence":
            label = <AccountRolePresenceFilterLabel filters={filters} />;
            break;

        case "accountRulePresence":
            label = <AccountRulePresenceFilterLabel filters={filters} />;
            break;

        case "form1099Boxes":
            label =
                selectionLabelVariant === "summary" ? (
                    <Form1099BoxSummaryLabel filters={filters} />
                ) : (
                    <Form1099BoxFilterLabel filters={filters} />
                );
            break;

        case "coaTemplateGroupKeys": {
            const count = filters.coaTemplateGroupKeys?.length ?? 0;
            label = <>{`${count} ${pluralize("group", count)}`}</>;
            break;
        }

        case "journalEntrySources":
            label = <JournalEntrySourceFilterLabel filters={filters} />;
            break;

        case "invoiceStatusFilter":
            label = (
                <CombinedStatusFilterLabel
                    statusFilter={readInvoiceStatusFilter(filters)}
                />
            );
            break;

        case "invoiceStatuses":
            label = <InvoiceStatusFilterLabel filters={filters} />;
            break;

        case "billableExpenseInvoiceStatuses":
            label = (
                <BillableExpenseInvoiceStatusFilterLabel filters={filters} />
            );
            break;

        case "catalogItems":
            label = <CatalogItemFilterLabel filters={filters} />;
            break;

        case "invoiceSourceTemplates":
            label = <InvoiceSourceTemplateFilterLabel filters={filters} />;
            break;

        case "incomeAccountIds":
            label = (
                <CatalogItemLedgerAccountFilterLabel
                    accountIds={filters.incomeAccountIds}
                    entityIds={filters.entityIds}
                />
            );
            break;

        case "receivablesAccountIds":
            label = (
                <CatalogItemLedgerAccountFilterLabel
                    accountIds={filters.receivablesAccountIds}
                    entityIds={filters.entityIds}
                />
            );
            break;

        case "isSubscription":
            label = <SubscriptionFilterLabel filters={filters} />;
            break;

        case "isEligible1099":
            label = <Eligible1099FilterLabel filters={filters} />;
            break;

        case "disabledFilter":
            label =
                filters.disabledFilter ===
                CounterpartyDisabledFilter.ONLY_DISABLED ? (
                    <>Only disabled</>
                ) : (
                    <>Only enabled</>
                );
            break;

        case "excludeCreditCardTransactions":
            label = <>No credit card</>;
            break;

        case "excludePaymentProcessorTransactions":
            label = <>No payment processor</>;
            break;

        case "includeExcludedAmounts":
            label = <>Excluded amounts hidden</>;
            break;

        case "excludeFrom1099":
            label =
                filters.excludeFrom1099 === true ? (
                    <>1099 excluded</>
                ) : (
                    <>1099 included</>
                );
            break;

        case "billStatusFilter":
            label = (
                <CombinedStatusFilterLabel
                    statusFilter={readBillStatusFilter(filters)}
                />
            );
            break;

        case "billStatuses":
            label = <BillStatusFilterLabel filters={filters} />;
            break;

        case "overdue":
            label = <OverDueDateFilterLabel filters={filters} />;
            break;

        case "due":
            label = <DueDateFilterLabel filters={filters} />;
            break;

        case "rulesStatus":
            label = <RulesStatusFilterLabel filters={filters} />;
            break;

        case "rulesSource":
            label = <RulesSourceFilterLabel filters={filters} />;
            break;

        case "ruleActionTypes":
            label = <RulesActionFilterLabel filters={filters} />;
            break;

        case "coaAccounts":
            label =
                selectionLabelVariant === "summary" ? (
                    <AccountSummaryLabel
                        accountIds={filters.coaAccounts}
                        noun="account"
                    />
                ) : (
                    <CoaAccountFilterLabel filters={filters} />
                );
            break;

        case "cashGeneralLedgerRoles":
            label = (
                <GeneralLedgerFilterLabel filters={filters} target="cash" />
            );
            break;

        case "accrualGeneralLedgerRoles":
            label = (
                <GeneralLedgerFilterLabel filters={filters} target="accrual" />
            );
            break;

        case "splitCoaAccounts":
            label = (
                <AccountSummaryLabel
                    accountIds={filters.splitCoaAccounts}
                    noun="split account"
                />
            );
            break;

        case "accrualCoaAccounts":
            label = <AccrualCoaAccountFilterLabel filters={filters} />;
            break;

        case "transactionIds":
            label = <TransactionIdsFilterLabel filters={filters} />;
            break;

        case "billIds":
            label = <BillIdsFilterLabel filters={filters} />;
            break;

        case "invoiceIds":
            label = <InvoiceIdsFilterLabel filters={filters} />;
            break;

        case "templateIds":
            label = <TemplateIdsFilterLabel filters={filters} />;
            break;

        case "startDate":
            label = <StartDateFilterLabel filters={filters} />;
            break;

        case "plans":
            label = <PlanFilterLabel filters={filters} />;
            break;

        case "whoPays":
            label = <WhoPaysFilterLabel filters={filters} />;
            break;

        case "services":
            label = <ServicesFilterLabel filters={filters} />;
            break;

        case "teamMembers":
            label = <TeamMembersFilterLabel filters={filters} />;
            break;

        case "assignee":
            label = <AssigneeFilterLabel filters={filters} />;
            break;

        case "legalType":
            label = <LegalTypeFilterLabel filters={filters} />;
            break;

        case "entityIndustries": {
            const industryLabel = formatFilterLabelText(
                (filters.entityIndustries ?? []).map(
                    (i) => industryLabels[i] ?? i,
                ),
                "Industries",
            );
            if (!industryLabel) {
                return null;
            }
            label = industryLabel;
            break;
        }

        case "entityBases": {
            const basisLabel = formatFilterLabelText(
                (filters.entityBases ?? []).map(getAccountingBasisLabel),
                "Bases",
            );
            if (!basisLabel) {
                return null;
            }
            label = basisLabel;
            break;
        }

        case "closeStage":
            label = <CloseStageFilterLabel filters={filters} />;
            break;

        case "priority":
            label = <PriorityFilterLabel filters={filters} />;
            break;

        case "closeDate":
            label = <CloseDateFilterLabel filters={filters} />;
            break;

        case "dueDate":
            label = <DueDateRangeFilterLabel filters={filters} />;
            break;

        case "hasUnread":
            label = <UnreadFilterLabel filters={filters} />;
            break;

        case "documentPeriod":
            label = <DocumentPeriodFilterLabel filters={filters} />;
            break;

        case "uploaderIds":
            label = <UploaderFilterLabel filters={filters} />;
            break;

        case "uploadDate":
            label = <DateFilterLabel filters={filters} />;
            break;

        case "fileExtensions":
            label = <DocumentTypeFilterLabel filters={filters} />;
            break;

        case "statuses":
            label = <TransactionStatusFilterLabel filters={filters} />;
            break;

        case "matchStatuses":
            label = <MatchStatusFilterLabel filters={filters} />;
            break;

        case "draftSources":
            label = (
                <DraftOptionsFilterLabel
                    pluralNoun="Sources"
                    options={draftSourceOptions}
                    values={filters.draftSources}
                />
            );
            break;

        case "draftCounterpartyStates":
            label = (
                <DraftCounterpartyFilterLabel
                    pluralNoun={`${getFilterTabLabel(
                        FilterTabs.DRAFT_COUNTERPARTY,
                        { tabLabels },
                    )} states`}
                    states={filters.draftCounterpartyStates}
                />
            );
            break;

        case "hasDueDate":
            label = filters.hasDueDate ? "Has due date" : "No due date";
            break;

        case "accrualReviewStatuses":
            label = (
                <DraftOptionsFilterLabel
                    pluralNoun="Statuses"
                    options={draftStatusOptions}
                    values={filters.accrualReviewStatuses}
                />
            );
            break;

        case "auditLogFilter":
            label = <>History filter</>;
            break;

        case "templateCounterparty":
            label = filters.hasCounterparty ? (
                <>Has counterparty</>
            ) : (
                <>Does not have counterparty</>
            );
            break;

        case "templateCustomer":
            label = filters.hasCustomer ? (
                <>Has billable customer</>
            ) : (
                <>Does not have billable customer</>
            );
            break;

        case "templateClass":
            label =
                filters.classFilterType === ClassFilterType.HAS_CLASSES ? (
                    <>Has class</>
                ) : (
                    <>Does not have a class</>
                );
            break;

        case "templateBasis": {
            const bases = filters.closeTemplateBases ?? [];
            if (bases.length === 0) {
                return null;
            }
            const basisLabels: Record<CloseTemplateBasis, string> = {
                [CloseTemplateBasis.ACCRUAL]: "Accrual",
                [CloseTemplateBasis.MODIFIED_CASH_LEDGER]: "Cash",
            };
            const basisText = formatFilterLabelText(
                bases.map((b) => basisLabels[b]),
                "Bases",
            );
            if (!basisText) {
                return null;
            }
            label = basisText;
            break;
        }

        case "taskAssignee":
            label = <TaskAssigneeFilterLabel filters={filters} />;
            break;

        case "taskCreatedBy":
            label = <TaskCreatedByFilterLabel filters={filters} />;
            break;

        case "taskClient":
            label = <ClientIdsFilterLabel clientIds={filters.taskClientIds} />;
            break;

        case "taskCreatedOn":
            label = <TaskCreatedOnFilterLabel filters={filters} />;
            break;

        case "taskCompletedBy":
            label = <TaskCompletedByFilterLabel filters={filters} />;
            break;

        case "taskCompletedOn":
            label = <TaskCompletedOnFilterLabel filters={filters} />;
            break;

        case "templateLastEditedBy":
            label = <TemplateLastEditedByFilterLabel filters={filters} />;
            break;

        case "templateOwner":
            label = <TemplateOwnerFilterLabel filters={filters} />;
            break;

        case "templateCreatedBy":
            label = <TemplateCreatedByFilterLabel filters={filters} />;
            break;

        case "taskClose":
            label = <TaskCloseFilterLabel filters={filters} />;
            break;

        case "taskPriority":
            label = <TaskPriorityFilterLabel filters={filters} />;
            break;

        case "taskAttachments":
            label = (
                <BooleanFilterLabel
                    value={filters.hasAttachments}
                    trueLabel="Has attachments"
                    falseLabel="Does not have attachments"
                />
            );
            break;

        case "taskGroup":
            label = <TaskGroupFilterLabel filters={filters} />;
            break;

        case "taskBlockedBy":
            label = (
                <BooleanFilterLabel
                    value={filters.hasBlockedBy}
                    trueLabel="Has blocked by"
                    falseLabel="Does not have blocked by"
                />
            );
            break;

        case "taskBlocking":
            label = (
                <BooleanFilterLabel
                    value={filters.hasBlocking}
                    trueLabel="Has blocking"
                    falseLabel="Does not have blocking"
                />
            );
            break;

        case "taskRelated":
            label = (
                <BooleanFilterLabel
                    value={filters.hasRelated}
                    trueLabel="Has related"
                    falseLabel="Does not have related"
                />
            );
            break;

        case "taskChat":
            label = (
                <BooleanFilterLabel
                    value={filters.hasUnreadChat}
                    trueLabel="Has unread chat"
                    falseLabel="Does not have unread chat"
                />
            );
            break;

        case "taskStage":
            label = <TaskStageFilterLabel filters={filters} />;
            break;

        case "orgTaskTemplateType":
            label = <OrgTaskTemplateTypeFilterLabel filters={filters} />;
            break;

        case "orgTaskTemplateGroup":
            label = <OrgTaskTemplateGroupFilterLabel filters={filters} />;
            break;

        case "orgTaskTemplateCreatedBy":
            label = <OrgTaskTemplateCreatedByFilterLabel filters={filters} />;
            break;

        case "orgTaskTemplateAssigneeRole":
            label = (
                <OrgTaskTemplateAssigneeRoleFilterLabel filters={filters} />
            );
            break;

        case "orgService":
            label = <OrgServiceFilterLabel filters={filters} />;
            break;

        case "orgCloseStage":
            label = <OrgCloseStageFilterLabel filters={filters} />;
            break;

        case "closeStatuses":
            label = (
                <EnumFilterLabel
                    values={filters.closeStatuses}
                    labels={closeStatusLabels}
                    pluralNoun="statuses"
                />
            );
            break;

        case "closeTemplate":
            label = <CloseTemplateFilterLabel filters={filters} />;
            break;

        case "applicationTypes":
            label = <ApplicationTypeFilterLabel filters={filters} />;
            break;

        case "progressStatuses":
            label = (
                <EnumFilterLabel
                    values={filters.progressStatuses}
                    labels={closeProgressStatusLabels}
                    pluralNoun="progress statuses"
                />
            );
            break;

        case "healthStatuses":
            label = (
                <EnumFilterLabel
                    values={filters.healthStatuses}
                    labels={closeHealthLabels}
                    pluralNoun="health statuses"
                />
            );
            break;

        case "workClient":
            label = <ClientIdsFilterLabel clientIds={filters.workClientIds} />;
            break;

        case "workStartDate":
            label = <WorkStartDateFilterLabel filters={filters} />;
            break;

        case "workDeliveryDate":
            label = <WorkDeliveryDateFilterLabel filters={filters} />;
            break;

        case "applicationDate":
            label = <ApplicationDateFilterLabel filters={filters} />;
            break;

        case "daysInStage":
            label = <DaysInStageFilterLabel filters={filters} />;
            break;

        case "reviewedFields":
            label = <ReviewFilterLabel filters={filters} mode="reviewed" />;
            break;

        case "unreviewedFields":
            label = <ReviewFilterLabel filters={filters} mode="unreviewed" />;
            break;

        case "reviewer":
            label = (
                <ReviewerFilterLabel
                    reviewerIds={filters.reviewerIds}
                    reviewerMode={filters.reviewerMode}
                />
            );
            break;

        case "ruleReviewStatuses":
            label = <RuleReviewFilterLabel filters={filters} />;
            break;

        case "ruleReviewer":
            label = (
                <ReviewerFilterLabel
                    reviewerIds={filters.ruleReviewerIds}
                    reviewerMode={filters.ruleReviewerMode}
                />
            );
            break;

        case "counterpartyReviewedFields":
            label = (
                <CounterpartyReviewFilterLabel
                    filters={filters}
                    mode="reviewed"
                />
            );
            break;

        case "counterpartyUnreviewedFields":
            label = (
                <CounterpartyReviewFilterLabel
                    filters={filters}
                    mode="unreviewed"
                />
            );
            break;

        case "counterpartyReviewer":
            label = (
                <ReviewerFilterLabel
                    reviewerIds={filters.counterpartyReviewerIds}
                    reviewerMode={filters.counterpartyReviewerMode}
                />
            );
            break;

        case "engagementTeam":
            label = <EngagementTeamFilterLabel filters={filters} />;
            break;

        case "schedulePeriod":
            label = <SchedulePeriodFilterLabel filters={filters} />;
            break;

        case "scheduleTemplate":
            label = <ScheduleTemplateFilterLabel filters={filters} />;
            break;

        case "scheduleClient":
            label = <ScheduleClientFilterLabel filters={filters} />;
            break;

        case "schedulesReportTemplate":
            label = <SchedulesReportTemplateFilterLabel filters={filters} />;
            break;

        case "schedulesReportGroup":
            label = <SchedulesReportGroupFilterLabel filters={filters} />;
            break;

        case "holdingAccount":
            label = <HoldingAccountFilterLabel filters={filters} />;
            break;

        case "termStart":
            label = (
                <TermDateFilterLabel
                    from={filters.termStartFrom}
                    to={filters.termStartTo}
                />
            );
            break;

        case "termEnd":
            label = (
                <TermDateFilterLabel
                    from={filters.termEndFrom}
                    to={filters.termEndTo}
                />
            );
            break;

        case "invoiceTerms": {
            const termsLabel = formatFilterLabelText(
                (filters.termTypes ?? []).map(
                    (termType) => NATIVE_INVOICE_TERM_TYPE_LABELS[termType],
                ),
                "Terms",
            );
            if (!termsLabel) {
                return null;
            }
            label = termsLabel;
            break;
        }

        case "invoiceSentStatus":
            label = <InvoiceSentFilterLabel filters={filters} />;
            break;

        case "invoiceCreatedOn":
            label = (
                <TermDateFilterLabel
                    from={filters.createdOnFrom}
                    to={filters.createdOnTo}
                />
            );
            break;

        case "invoiceDueDate":
            label = (
                <TermDateFilterLabel
                    from={filters.dueDateFrom}
                    to={filters.dueDateTo}
                />
            );
            break;

        case "invoiceDate":
            label = (
                <TermDateFilterLabel
                    from={filters.invoiceDateFrom}
                    to={filters.invoiceDateTo}
                />
            );
            break;

        case "agingPeriod": {
            const agingPeriodLabel = formatFilterLabelText(
                (filters.agingBuckets ?? []).map(
                    (bucket) => AR_AGING_BUCKET_LABELS[bucket],
                ),
                "Aging periods",
            );
            if (!agingPeriodLabel) {
                return null;
            }
            label = agingPeriodLabel;
            break;
        }

        case "templateType": {
            const templateTypeLabels: Record<string, string> = {
                general: "General",
                client_specific: "Client specific",
            };
            const templateTypeLabel = filters.closeTemplateType
                ? (templateTypeLabels[filters.closeTemplateType] ??
                  filters.closeTemplateType)
                : null;
            if (!templateTypeLabel) {
                return null;
            }
            label = templateTypeLabel;
            break;
        }

        case "accountClasses": {
            const accountClassesLabel = formatFilterLabelText(
                filters.accountClasses ?? [],
                "Ledger types",
            );
            if (!accountClassesLabel) {
                return null;
            }
            label = accountClassesLabel;
            break;
        }
    }

    return label;
};

export const ActiveFilterButtonLabel: React.FC<Props> = ({
    filterId,
    filters,
    onChange,
    disabled = false,
    ...labelProps
}) => {
    const { open: openFilter } = useAggregatedFiltersContext();
    const resetFilter = useResetFilters(resetPayload, onChange, filterId);

    const label = getActiveFilterLabel({ filterId, filters, ...labelProps });
    if (label === null) {
        return null;
    }

    const filterTab = ActiveFilterToFilterTabsMap[filterId];
    const isVirtualTab = VIRTUAL_FILTER_TABS.includes(filterTab);

    return (
        <ActiveFilterButton
            openFilter={
                disabled || isVirtualTab
                    ? undefined
                    : () => openFilter(filterTab)
            }
            resetFilter={resetFilter}
            label={label}
            disabled={disabled}
        />
    );
};
